import { Activity, Bot, Briefcase, FlaskConical, Hash, Heart, Settings, Star, Wallet } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

interface ProfileUser {
  id: number
  username: string
  display_name: string
  bio?: string
  role?: string
  level?: number
  total_xp?: number
  followers_count?: number
  following_count?: number
  posts_count?: number
  agents_count?: number
  created_at?: string
  avatar_url?: string
  cover_color?: string
}

interface AgentItem {
  id: number
  name: string
  status: string
  level: number
  llm_type?: string
}

interface PostItem {
  id: number
  content: string
  post_type: string
  created_at: string
  like_count: number
  comment_count: number
  skill_tags?: string[]
}

interface ResearchItem {
  id: number
  title: string
  status: string
  domain?: string
  created_at: string
  total_findings?: number
}

interface WorkItem {
  id: number
  title: string
  status: string
  category?: string
  created_at: string
  flz_reward?: number
}

interface ActivityItem {
  id: number
  action: string
  target_type: string
  target_id: number
  created_at: string
  details?: string
}

type Tab = 'posts' | 'agents' | 'research' | 'work' | 'activity'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  completed: { bg: 'rgba(102,126,234,0.12)', color: '#667eea' },
  pending: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  cancelled: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444' },
  failed: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444' },
  in_progress: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
}

function statusStyle(s: string) {
  return STATUS_COLORS[s] ?? { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' }
}

export default function UserProfile() {
  const { username, userId } = useParams<{ username: string; userId: string }>()
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()

  const [profile, setProfile] = useState<ProfileUser | null>(null)
  const [agents, setAgents] = useState<AgentItem[]>([])
  const [posts, setPosts] = useState<PostItem[]>([])
  const [research, setResearch] = useState<ResearchItem[]>([])
  const [work, setWork] = useState<WorkItem[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [tabLoading, setTabLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('posts')

  const target = userId ?? username ?? currentUser?.username
  const isOwn = userId
    ? String(currentUser?.id) === userId
    : currentUser?.username === username || (!username && !!currentUser)

  const token = localStorage.getItem('token') ?? ''

  const load = useCallback(() => {
    if (!target) {return}
    setLoading(true)
    fetch(`${API_URL}/api/users/${target}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.ok ? r.json() as Promise<ProfileUser> : null)
      .then(d => { setProfile(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [target, token])

  useEffect(() => { void load() }, [load])

  // Agents
  useEffect(() => {
    if (!profile?.id) {return}
    fetch(`${API_URL}/api/agents/my?user_id=${profile.id}`)
      .then(r => r.ok ? r.json() as Promise<AgentItem[]> : [])
      .then(d => setAgents(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [profile?.id])

  // Posts
  useEffect(() => {
    if (!profile?.id || activeTab !== 'posts') {return}
    setTabLoading(true)
    fetch(`${API_URL}/api/social/posts?user_id=${profile.id}&limit=20`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then((d: { posts: PostItem[] }) => { setPosts(d.posts ?? []); setTabLoading(false) })
      .catch(() => setTabLoading(false))
  }, [profile?.id, activeTab])

  // Research
  useEffect(() => {
    if (!profile?.id || activeTab !== 'research') {return}
    setTabLoading(true)
    fetch(`${API_URL}/api/v1/research-projects/?user_id=${profile.id}&per_page=20`)
      .then(r => r.ok ? r.json() : { projects: [] })
      .then((d: { projects: ResearchItem[] }) => { setResearch(d.projects ?? []); setTabLoading(false) })
      .catch(() => setTabLoading(false))
  }, [profile?.id, activeTab])

  // Work
  useEffect(() => {
    if (!profile?.id || activeTab !== 'work') {return}
    if (!token) { setTabLoading(false); return }
    setTabLoading(true)
    fetch(`${API_URL}/api/work/tasks?limit=20`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { tasks: [] })
      .then((d: { tasks: WorkItem[] }) => { setWork(d.tasks ?? []); setTabLoading(false) })
      .catch(() => setTabLoading(false))
  }, [profile?.id, activeTab, token])

  // Activity
   
  useEffect(() => {
    if (!profile?.id || activeTab !== 'activity') {return}
    setTabLoading(true)
    fetch(`${API_URL}/api/users/${profile.id}/activity?limit=20`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.ok ? r.json() : [])
      .then((d: ActivityItem[]) => { setActivity(Array.isArray(d) ? d : []); setTabLoading(false) })
      .catch(() => setTabLoading(false))
  }, [profile?.id, activeTab, token])

  if (loading) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      </MainLayout>
    )
  }

  if (!profile) {
    return (
      <MainLayout>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>User not found.</div>
      </MainLayout>
    )
  }

  const displayName = profile.display_name || profile.username
  const coverGradient = profile.cover_color
    ? `linear-gradient(135deg, ${profile.cover_color}55, ${profile.cover_color}99)`
    : 'linear-gradient(135deg, #667eea55, #764ba299)'

  const tabs: { key: Tab; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'posts', label: 'Posts', icon: <Hash size={14} />, count: profile.posts_count },
    { key: 'agents', label: 'Agents', icon: <Bot size={14} />, count: agents.length },
    { key: 'research', label: 'Research', icon: <FlaskConical size={14} /> },
    { key: 'work', label: 'Work', icon: <Briefcase size={14} /> },
    { key: 'activity', label: 'Activity', icon: <Activity size={14} /> },
  ]

  function formatDate(s: string) {
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return s }
  }

  return (
    <MainLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* Cover + Profile */}
        <div style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
          <div style={{ height: '130px', background: coverGradient, position: 'relative' }}>
            {isOwn && (
              <button
                onClick={() => navigate('/settings')}
                style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', background: 'rgba(0,0,0,0.3)', border: 'none', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(8px)' }}
              >
                <Settings size={13} /> Edit Profile
              </button>
            )}
          </div>

          <div style={{ padding: '0 24px 24px', position: 'relative' }}>
            <div style={{
              width: 76, height: 76, borderRadius: '50%',
              background: profile.cover_color ? `linear-gradient(135deg, ${profile.cover_color}, #764ba2)` : 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '28px', fontWeight: 800,
              border: '4px solid var(--card-bg)',
              marginTop: '-38px', marginBottom: '12px', flexShrink: 0
            }}>
              {profile.avatar_url
                ? <img src={profile.avatar_url} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : displayName[0]?.toUpperCase()
              }
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{displayName}</h1>
                  {profile.role === 'admin' && (
                    <span style={{ fontSize: '11px', padding: '2px 8px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: '6px', fontWeight: 700 }}>Admin</span>
                  )}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '10px' }}>@{profile.username}</div>

                {/* Level & XP Bar */}
                <div style={{ marginBottom: '16px', maxWidth: '280px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, marginBottom: '6px' }}>
                    <span style={{ color: '#6366f1' }}>LEVEL {profile.level || 1}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{(profile.total_xp || 0) % 1000} / 1000 XP</span>
                  </div>
                  <div style={{ height: '8px', background: 'var(--body-bg)', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border-color)', position: 'relative' }}>
                    <div style={{
                      width: `${Math.min(100, ((profile.total_xp || 0) % 1000) / 10)}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #6366f1, #a855f7)',
                      boxShadow: '0 0 12px rgba(99,102,241,0.4)',
                      transition: 'width 1s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }} />
                  </div>
                </div>

                {profile.bio && (
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: '500px', margin: 0 }}>{profile.bio}</p>
                )}
              </div>
              {isOwn && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => navigate('/dashboard?tab=settings')}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}
                  >
                    <Settings size={14} /> Settings
                  </button>
                  <button
                    onClick={() => navigate('/dashboard?tab=wallet')}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}
                  >
                    <Wallet size={14} /> Wallet
                  </button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '24px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
              {[
                { icon: <Hash size={13} />, val: profile.posts_count ?? 0, label: 'Posts' },
                { icon: <Star size={13} />, val: profile.total_xp ?? 0, label: 'XP' },
                { icon: <Heart size={13} />, val: profile.followers_count ?? 0, label: 'Followers' },
                { icon: <Activity size={13} />, val: profile.following_count ?? 0, label: 'Following' },
                { icon: <Bot size={13} />, val: agents.length, label: 'Agents' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{s.val}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '3px' }}>{s.icon}{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '5px' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: '7px', fontSize: '13px',
                fontWeight: 600, cursor: 'pointer', border: 'none',
                background: activeTab === tab.key ? '#667eea' : 'transparent',
                color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
              }}
            >
              {tab.icon}{tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '10px', background: activeTab === tab.key ? 'rgba(255,255,255,0.25)' : 'rgba(102,126,234,0.12)', color: activeTab === tab.key ? '#fff' : '#667eea', fontWeight: 700 }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', minHeight: '200px' }}>
          {tabLoading && (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
          )}

          {/* POSTS */}
          {!tabLoading && activeTab === 'posts' && (
            posts.length === 0
              ? <EmptyState icon={<Hash size={28} />} label="No posts yet" action={isOwn ? { label: 'Go to Social Feed', onClick: () => navigate('/social') } : undefined} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {posts.map(p => (
                    <div
                      key={p.id}
                      onClick={() => navigate(`/social/post/${p.id}`)}
                      style={{ padding: '14px', borderRadius: '10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
                    >
                      <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: '8px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {p.content}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        <span>{formatDate(p.created_at)}</span>
                        <span>♥ {p.like_count ?? 0}</span>
                        <span>💬 {p.comment_count ?? 0}</span>
                        {p.post_type && p.post_type !== 'general' && (
                          <span style={{ padding: '1px 7px', borderRadius: '5px', background: 'rgba(102,126,234,0.1)', color: '#667eea', fontSize: '11px', fontWeight: 600 }}>{p.post_type}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}

          {/* AGENTS */}
          {!tabLoading && activeTab === 'agents' && (
            agents.length === 0
              ? <EmptyState icon={<Bot size={28} />} label="No agents yet" action={isOwn ? { label: 'Create Agent', onClick: () => navigate('/agents/new') } : undefined} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {agents.map(a => (
                    <div
                      key={a.id}
                      onClick={() => navigate(`/agent/${a.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                        {a.name?.[0]?.toUpperCase() ?? 'A'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{a.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{a.llm_type ?? 'AI'} · Lv {a.level}</div>
                      </div>
                      <div style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', ...statusStyle(a.status), fontWeight: 600 }}>
                        {a.status}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}

          {/* RESEARCH */}
          {!tabLoading && activeTab === 'research' && (
            research.length === 0
              ? <EmptyState icon={<FlaskConical size={28} />} label="No research projects yet" action={isOwn ? { label: 'Start Research', onClick: () => navigate('/research') } : undefined} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {research.map(r => (
                    <div
                      key={r.id}
                      onClick={() => navigate(`/research/${r.id}`)}
                      style={{ padding: '14px', borderRadius: '10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{r.title}</div>
                        <div style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', ...statusStyle(r.status), fontWeight: 600, flexShrink: 0 }}>{r.status}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {r.domain && <span>{r.domain}</span>}
                        <span>{formatDate(r.created_at)}</span>
                        {r.total_findings !== undefined && <span>{r.total_findings} findings</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}

          {/* WORK */}
          {!tabLoading && activeTab === 'work' && (
            work.length === 0
              ? <EmptyState icon={<Briefcase size={28} />} label="No work tasks yet" action={isOwn ? { label: 'Create Work Task', onClick: () => navigate('/work/create') } : undefined} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {work.map(w => (
                    <div
                      key={w.id}
                      onClick={() => navigate(`/work/${w.id}`)}
                      style={{ padding: '14px', borderRadius: '10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{w.title}</div>
                        <div style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', ...statusStyle(w.status), fontWeight: 600, flexShrink: 0 }}>{w.status}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {w.category && <span>{w.category}</span>}
                        <span>{formatDate(w.created_at)}</span>
                        {w.flz_reward !== undefined && <span>{w.flz_reward} FLZ</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}

          {/* ACTIVITY */}
          {!tabLoading && activeTab === 'activity' && (
            activity.length === 0
              ? <EmptyState icon={<Activity size={28} />} label="No activity yet" />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {activity.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: 'var(--body-bg)', border: '1px solid var(--border-color)' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#667eea', flexShrink: 0 }} />
                      <div style={{ flex: 1, fontSize: '13px', color: 'var(--text-primary)' }}>{a.action} {a.details ?? ''}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', flexShrink: 0 }}>{formatDate(a.created_at)}</div>
                    </div>
                  ))}
                </div>
              )
          )}
        </div>
      </div>
    </MainLayout>
  )
}

function EmptyState({ icon, label, action }: { icon: React.ReactNode; label: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)', fontSize: '13px' }}>
      <div style={{ margin: '0 auto 10px', display: 'flex', justifyContent: 'center', opacity: 0.35 }}>{icon}</div>
      <div style={{ fontWeight: 600, marginBottom: '8px' }}>{label}</div>
      {action && (
        <button onClick={action.onClick} style={{ padding: '8px 16px', borderRadius: '8px', background: '#667eea', border: 'none', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          {action.label}
        </button>
      )}
    </div>
  )
}
